// Libs
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import {
  useForm,
  SubmitHandler,
  FormProvider,
  useWatch,
} from "react-hook-form";
import { z } from "zod";
import { v4 as uuid4 } from "uuid";

// Local
import {
  adminPropDefaultsForCustomField,
  CustomFieldManager,
  SelectionCustomField,
} from "../../../models/custom-field";
import {
  SelectionSchema_RHF,
  FormComponentInterface,
  isDefinedOnTaskStatus,
  TaskTypeField,
  TitleField,
  SelectionDefaultValueField,
  EditableAndRequiredFields,
  OnTaskStatusField,
  ActionButtons,
  SelectionOptionsField,
  HideOnCRCreationField,
} from "./AddCustomFieldDialog";
import { ErrorMessage } from "../../ErrorMessage";
import { logger as devLogger } from "../../../logging";
import { OTaskTypes } from "../../../models/task-types";

const selectionDefaultValues: Partial<SelectionInterface> = {
  defaultValue: "",

  title: "",
  editable: false,
  required: false,
  hideOnCraftRecordCreation: false,
};
type SelectionInterface = z.infer<typeof SelectionSchema_RHF>;

export const SelectionForm: React.FunctionComponent<FormComponentInterface> = (
  props,
) => {
  const methods = useForm<SelectionInterface>({
    defaultValues: selectionDefaultValues,
    resolver: zodResolver(SelectionSchema_RHF),
    mode: "onChange",
  });

  const watchOptions = useWatch({
    control: methods.control,
    name: "selectionOptions",
  });

  useEffect(() => {
    methods.reset(selectionDefaultValues);
  }, [methods]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayError, setDisplayError] = useState<boolean>(false);

  const watchTaskType = useWatch({
    control: methods.control,
    name: "taskType",
  });

  const onSave: SubmitHandler<SelectionInterface> = async (formValues) => {
    // Save button indicator
    setIsSubmitting(true);

    if (props.workType === undefined) throw Error("workType is undefined");

    // If it's an empty string, they didn't input anything. Save value as null.
    const defaultValue =
      formValues.defaultValue.length === 0 ? null : formValues.defaultValue;

    // Drop undefined values from the onTaskStatus list.
    // onTaskStatus itself will be undefined if this is a workType.
    const onTaskStatus = formValues.onTaskStatus?.filter(isDefinedOnTaskStatus);

    const selectionOptions = formValues.selectionOptions.reduce((acc, curr) => {
      const currentValue = Object.values(curr)[0];
      // Don't add empty strings to the output data.
      if (currentValue.length === 0) return acc;
      const newKey = uuid4();
      acc[newKey] = currentValue;
      return acc;
    }, {});

    // Compose the output structure.
    let fullOutput: SelectionCustomField;

    const partialOutput: Omit<
      SelectionCustomField,
      "taskType" | "craftRecordOrTask"
    > = {
      ...adminPropDefaultsForCustomField(),
      fieldType: "selection",
      defaultValue: defaultValue,
      title: formValues.title,
      editable: formValues.editable,
      required: formValues.required,
      selectionOptions: selectionOptions,
      craftType: props.workType,
    };

    // If taskType is 0, they selected "None". This means it's a craftRecord, and
    // we don't want to save the taskType to the database.
    if (formValues.taskType === 0) {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "craftRecord",
        hideOnCraftRecordCreation: formValues.hideOnCraftRecordCreation,
      };
    } else {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "task",
        taskType: formValues.taskType,
        onTaskStatus: onTaskStatus,
      };
    }

    // Validate CustomFieldDocData
    const validatedOutput = CustomFieldManager.parse(fullOutput);

    try {
      await props.handleSave(validatedOutput);

      setIsSubmitting(false);

      // Close dialog if successful.
      props.closeDialog();
    } catch (e) {
      setIsSubmitting(false);
      setDisplayError(true);
      devLogger.error(e);
    }
  };

  const errorMessage = (
    <ErrorMessage
      message="Something went wrong."
      clearMessage={() => setDisplayError(false)}
    />
  );

  return (
    <FormProvider {...methods}>
      <form
        className="contents"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSave)}
      >
        <hr className="mt-4 w-full border border-dashed border-gray-300 sm:col-span-2 lg:col-span-3" />
        <TaskTypeField
          control={methods.control}
          errors={methods.formState.errors}
          workType={props.workType}
        />

        <TitleField
          control={methods.control}
          errors={methods.formState.errors}
        />

        {/* Specific to fieldType selection */}
        <SelectionOptionsField errors={methods.formState.errors} />
        <SelectionDefaultValueField
          control={methods.control}
          errors={methods.formState.errors}
          selectionOptions={watchOptions}
        />

        <EditableAndRequiredFields control={methods.control} />

        {watchTaskType === 0 && (
          <HideOnCRCreationField control={methods.control} />
        )}

        {watchTaskType != null &&
          watchTaskType !== 0 &&
          Object.values(OTaskTypes).includes(watchTaskType) && (
            <OnTaskStatusField
              control={methods.control}
              taskStatusList={props.taskStatusList}
            />
          )}

        <span className="-my-5 h-10 justify-self-center sm:col-span-2 lg:col-span-3">
          {displayError ? errorMessage : null}
        </span>

        <ActionButtons submitting={isSubmitting} onCancel={props.closeDialog} />
      </form>
    </FormProvider>
  );
};
